<template>
  <div
    class="h-screen bg-background flex overflow-hidden"
    :class="{ 'sidebar-collapsed': sidebarCollapsed }"
  >
    <div
      v-if="!sidebarCollapsed && isMobile"
      @click="sidebarCollapsed = true"
      class="fixed inset-0 bg-black bg-opacity-50 z-[120] md:hidden"
    ></div>

    <div
      class="sidebar-wrapper transition-all duration-300 ease-in-out"
      :class="[
        sidebarCollapsed ? 'w-16' : 'w-64 md:w-72',
        isMobile && !sidebarCollapsed ? 'fixed inset-y-0 left-0 z-[130]' : '',
        isMobile && sidebarCollapsed
          ? 'fixed inset-y-0 left-0 z-[130] -translate-x-full'
          : '',
      ]"
    >
      <div
        class="sidebar bg-surface h-full flex flex-col shadow-lg rounded-r-3xl overflow-hidden border-r border-outline-variant/20"
      >
        <div class="px-4 pt-4 pb-2 flex items-center">
          <div class="flex items-center min-w-0">
            <div
              class="flex-shrink-0 h-9 w-24 overflow-hidden rounded-lg"
              :class="{ 'mx-auto': sidebarCollapsed }"
            >
              <img
                src="/images/bi-logo.jpg"
                alt="Brand Integrated"
                class="h-full w-full object-contain object-left"
              />
            </div>
            <h1
              class="ml-3 text-xl font-medium text-on-surface transition-opacity duration-300"
              :class="{ 'opacity-0': sidebarCollapsed }"
            >
              Admin
            </h1>
          </div>
        </div>

        <nav
          class="flex-1 mt-2 px-2 space-y-1 overflow-y-auto"
          :class="{ 'px-1': sidebarCollapsed }"
        >
          <router-link
            to="/admin/dashboard"
            class="flex items-center py-3 rounded-2xl transition-all duration-200 hover:bg-primary/10"
            :class="[
              $route.name === 'Dashboard' || $route.name === 'ArticleCreate' || $route.name === 'ArticleEdit'
                ? 'bg-primary/10 text-primary'
                : 'text-on-surface-variant',
              sidebarCollapsed ? 'justify-center px-3' : 'px-4',
            ]"
          >
            <svg
              class="w-6 h-6 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span v-if="!sidebarCollapsed" class="ml-3 text-sm font-medium">Articles</span>
          </router-link>

          <router-link
            to="/admin/users"
            class="flex items-center py-3 rounded-2xl transition-all duration-200 hover:bg-primary/10"
            :class="[
              $route.name === 'AdminUsers' || $route.name === 'AdminUserCreate' || $route.name === 'AdminUserEdit'
                ? 'bg-primary/10 text-primary'
                : 'text-on-surface-variant',
              sidebarCollapsed ? 'justify-center px-3' : 'px-4',
            ]"
          >
            <svg
              class="w-6 h-6 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <span v-if="!sidebarCollapsed" class="ml-3 text-sm font-medium">Users</span>
          </router-link>

          <router-link
            to="/admin/analytics"
            class="flex items-center py-3 rounded-2xl transition-all duration-200 hover:bg-primary/10"
            :class="[
              $route.name === 'Analytics' ? 'bg-primary/10 text-primary' : 'text-on-surface-variant',
              sidebarCollapsed ? 'justify-center px-3' : 'px-4',
            ]"
          >
            <svg
              class="w-6 h-6 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span v-if="!sidebarCollapsed" class="ml-3 text-sm font-medium">Analytics</span>
          </router-link>
        </nav>

        <div class="border-t border-outline-variant/30 p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center" v-if="!sidebarCollapsed">
              <img
                :src="userAvatar"
                alt="User avatar"
                class="w-10 h-10 rounded-full object-cover border-2 border-primary/20"
              />
              <div class="ml-3">
                <p class="text-sm font-medium text-on-surface">
                  {{ currentUser?.fullName || currentUser?.email || "User" }}
                </p>
              </div>
            </div>
            <button
              @click="handleLogout"
              class="flex items-center justify-center text-on-surface-variant hover:text-error focus:outline-none transition-colors duration-200"
              :class="{ 'w-full': sidebarCollapsed }"
            >
              <img
                v-if="sidebarCollapsed"
                :src="userAvatar"
                alt="User avatar"
                class="w-10 h-10 rounded-full object-cover border-2 border-primary/20"
              />
              <svg
                v-else
                class="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex-1 flex flex-col overflow-hidden transition-all duration-300"
      :class="{ 'md:ml-0': isMobile }"
    >
      <header class="bg-surface shadow-sm z-10 border-b border-outline-variant/20">
        <div class="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div class="flex items-center">
            <button
              @click="sidebarCollapsed = !sidebarCollapsed"
              class="mr-3 p-2 rounded-lg bg-surface-container-low text-on-surface-variant hover:text-primary hover:bg-primary/10 focus:outline-none transition-all duration-200"
            >
              <svg
                class="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  :d="
                    sidebarCollapsed
                      ? 'M13 5l7 7-7 7M5 5l7 7-7 7'
                      : 'M11 19l-7-7 7-7M19 19l-7-7 7-7'
                  "
                />
              </svg>
            </button>
            <h1 class="text-xl font-medium text-on-surface">{{ pageTitle }}</h1>
          </div>
          <div class="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              class="header-view-site-btn flex items-center gap-2 text-sm"
              aria-label="View site (opens in new tab)"
            >
              <svg
                class="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              <span class="hidden sm:inline">View Site</span>
            </a>
            <slot name="header-actions" />
          </div>
        </div>
      </header>

      <main
        class="flex-1 overflow-auto bg-background p-4 sm:p-6 lg:p-8 min-h-0"
        :class="fill ? 'flex flex-col' : ''"
      >
        <slot />
      </main>
    </div>

    <AdminToast />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { authService } from "../../utils/auth.js";
import { consumeAdminFlash } from "../../utils/adminToast.js";
import AdminToast from "./AdminToast.vue";

defineProps({
  pageTitle: {
    type: String,
    required: true,
  },
  fill: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const sidebarCollapsed = ref(
  localStorage.getItem("sidebarCollapsed") === "true" || false
);
const isMobile = ref(false);
const currentUser = ref(null);

const userAvatar = computed(() => {
  const name =
    currentUser.value?.fullName || currentUser.value?.email || "User";
  return (
    "https://ui-avatars.com/api/?name=" +
    encodeURIComponent(name) +
    "&background=eef2ff&color=4338ca"
  );
});

watch(sidebarCollapsed, (newValue) => {
  localStorage.setItem("sidebarCollapsed", newValue.toString());
});

const handleResize = () => {
  const wasMobile = isMobile.value;
  isMobile.value = window.innerWidth < 768;

  if (isMobile.value && !wasMobile) {
    sidebarCollapsed.value = true;
  } else if (!isMobile.value && wasMobile) {
    const savedState = localStorage.getItem("sidebarCollapsed");
    sidebarCollapsed.value = savedState === "true";
  }
};

watch(
  () => router.currentRoute.value.path,
  () => {
    if (isMobile.value && !sidebarCollapsed.value) {
      sidebarCollapsed.value = true;
    }
  }
);

const handleLogout = () => {
  authService.logout();
  router.push("/admin/login");
};

onMounted(() => {
  if (!authService.isAuthenticated()) {
    router.push("/admin/login");
    return;
  }

  handleResize();
  window.addEventListener("resize", handleResize);
  currentUser.value = authService.getCurrentUser();
});

watch(
  () => route.fullPath,
  () => {
    if (route.path.startsWith("/admin") && !route.meta?.requiresGuest) {
      consumeAdminFlash();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.sidebar-wrapper {
  will-change: width;
}

.sidebar nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar nav::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}

.header-view-site-btn {
  padding: 0.5rem 1.1rem;
  border-radius: 9999px;
  border: 1px solid rgba(21, 66, 18, 0.2);
  background: rgba(255, 255, 255, 0.9);
  color: #154212;
  font-weight: 500;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.header-view-site-btn:hover {
  background: #f5f3f3;
  border-color: #154212;
}

.sidebar nav a,
.sidebar nav a:hover,
.sidebar nav a:focus,
.sidebar nav a.router-link-active,
.sidebar nav a.router-link-exact-active {
  text-decoration: none;
}
</style>
